import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Send } from 'lucide-react';
import { createContract, buildContractUrl } from '../../lib/contract-queries';
import { whatsAppLink } from '../../lib/phone';
import { supabase } from '../../lib/supabase';
import type { Property } from '../../types/types';

interface Props {
  ownerId: string;
  ownerName: string;
  ownerPhone?: string;
  properties: Property[];   // the owner's properties (picker options)
  onClose: () => void;
  onCreated: () => void;    // refresh the contracts list
}

// Default commission: 5% for sale, 100% (one month) for rent/vacacional.
function defaultCommission(operation?: string): number {
  return operation === 'rent' || operation === 'vacational' ? 100 : 5;
}

// Canned intro messages the agent can insert with one tap instead of retyping
// the same copy on every contract. Shown above the personalized-message field.
const SUGGESTED_INTROS = [
  'Por favor completar el Acuerdo de Intermediación Inmobiliaria.',
];

export function OwnerContractModal({ ownerId, ownerName, ownerPhone, properties, onClose, onCreated }: Props) {
  const [propertyId, setPropertyId] = useState<string>(properties.length === 1 ? String(properties[0].id) : '');
  const selectedProp = useMemo(() => properties.find(p => String(p.id) === propertyId), [properties, propertyId]);
  const [commission, setCommission] = useState<number>(defaultCommission(properties.length === 1 ? properties[0].operation : undefined));
  const [intro, setIntro] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasOwnerPhone = !!(ownerPhone && ownerPhone.trim());
  const [phoneInput, setPhoneInput] = useState('');

  // Re-default the commission when the agent picks a property.
  const onPickProperty = (id: string) => {
    setPropertyId(id);
    const op = properties.find(p => String(p.id) === id)?.operation;
    setCommission(defaultCommission(op));
  };

  const handleSend = async () => {
    if (!propertyId) { setError('Selecciona una propiedad.'); return; }
    if (!hasOwnerPhone && !phoneInput.trim()) { setError('Ingresa el teléfono del propietario.'); return; }
    setError(null);
    setSending(true);
    try {
      const res = await createContract(ownerId, propertyId, intro, commission);
      // Persist a newly-entered phone to the owner's contact.
      if (!hasOwnerPhone && phoneInput.trim() && supabase) {
        await supabase.from('contacts').update({ phone: phoneInput.trim() }).eq('id', ownerId);
      }
      const url = buildContractUrl(res.id);
      const message = `Estimado(a) ${res.owner_name || ownerName} en el siguiente enlace se encuentra el formulario de la promoción de la propiedad.\n${url}`;
      // Picker fallback (wa.me/?text=…) when the owner phone can't be confidently
      // normalized — avoids messaging a number WhatsApp would misread.
      window.open(whatsAppLink(res.owner_phone || ownerPhone || phoneInput, message), '_blank');
      onCreated();
      onClose();
    } catch (e) {
      setError((e as Error).message || 'No se pudo generar el contrato.');
    } finally {
      setSending(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-black text-slate-900">Enviar Contrato</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={18} /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Property picker */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5">Propiedad</label>
            {properties.length === 0 ? (
              <p className="text-sm text-slate-500">
                Este propietario no es dueño de ninguna propiedad. Asígnalo como <b>Propietario</b> en la
                ficha de la propiedad (Editar → Detalles → Propietario) y volverá a aparecer aquí.
              </p>
            ) : (
              <select
                value={propertyId}
                onChange={(e) => onPickProperty(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 font-medium focus:outline-none focus:border-indigo-500"
              >
                <option value="" disabled>Selecciona una propiedad</option>
                {properties.map(p => (
                  <option key={String(p.id)} value={String(p.id)}>{p.title || p.ii_id || `Propiedad ${p.id}`}</option>
                ))}
              </select>
            )}
          </div>

          {/* Commission */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5">Comisión (%)</label>
            <input
              type="number" min={0} max={100} step={0.5}
              value={commission}
              onChange={(e) => setCommission(Number(e.target.value))}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 font-bold focus:outline-none focus:border-indigo-500"
            />
            <p className="text-[11px] text-slate-400 mt-1">Predeterminado: 5% venta · 100% alquiler. Puedes ajustarlo.</p>
          </div>

          {/* Owner phone — only when the contact has none on file. Required to
              send via WhatsApp; saved back to the contact on send. */}
          {!hasOwnerPhone && (
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5">Teléfono del propietario (WhatsApp)</label>
              <input
                type="tel" inputMode="tel"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                placeholder="5555 5555"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 font-bold focus:outline-none focus:border-indigo-500"
              />
              <p className="text-[11px] text-slate-400 mt-1">Este propietario no tiene teléfono guardado. Se guardará en su ficha.</p>
            </div>
          )}

          {/* Personalized intro */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5">Mensaje personalizado (opcional)</label>
            {/* One-tap suggestions so the agent doesn't retype common copy. */}
            <div className="flex flex-wrap gap-2 mb-2">
              {SUGGESTED_INTROS.map((s) => {
                const active = intro.trim() === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setIntro(active ? '' : s)}
                    className={`text-left px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${active ? 'bg-indigo-500/10 text-indigo-600 border-indigo-500/40' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:bg-slate-50'}`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
            <textarea
              value={intro}
              onChange={(e) => setIntro(e.target.value)}
              rows={3}
              placeholder="Aparecerá al inicio del formulario que verá el propietario."
              className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 text-sm focus:outline-none focus:border-indigo-500 resize-none"
            />
          </div>

          {error && <p className="text-sm font-medium text-rose-600 bg-rose-50 rounded-xl px-3 py-2">{error}</p>}

          <button
            onClick={handleSend}
            disabled={sending || !propertyId}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-black text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed bg-emerald-600 hover:bg-emerald-700"
          >
            {sending ? <Loader2 className="animate-spin" size={18} /> : <Send size={16} />}
            {sending ? 'Generando…' : 'Generar y enviar por WhatsApp'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
